import { useEffect } from 'react';

interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  canonical?: string;
  type?: 'website' | 'article';
  noindex?: boolean; // For pages in development
}

export const usePageSEO = (config: SEOConfig) => {
  useEffect(() => {
    // Update document title
    document.title = config.title;

    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, name);
        document.head.appendChild(tag);
      }
      
      tag.setAttribute('content', content);
    };

    // Robots tag (for development pages)
    if (config.noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists
      const robotsTag = document.querySelector('meta[name="robots"]');
      if (robotsTag && robotsTag.getAttribute('content')?.includes('noindex')) {
        robotsTag.remove();
      }
    }

    // Standard meta tags
    updateMetaTag('description', config.description);
    if (config.keywords) updateMetaTag('keywords', config.keywords);

    // Open Graph tags
    updateMetaTag('og:title', config.title, true);
    updateMetaTag('og:description', config.description, true);
    updateMetaTag('og:type', config.type || 'website', true);
    if (config.image) updateMetaTag('og:image', config.image, true);
    if (config.canonical) updateMetaTag('og:url', config.canonical, true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', config.title);
    updateMetaTag('twitter:description', config.description);
    if (config.image) updateMetaTag('twitter:image', config.image);

    // Canonical URL
    if (config.canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', config.canonical);
    }
  }, [config.title, config.description, config.keywords, config.image, config.canonical, config.type, config.noindex]);
};
